import React, { useCallback, useState } from "react";
import {
  useFormChangeHandler,
  useFormChangeHandlerV2,
} from "hooks/useFormChangeHandler";
import { useCreateData } from "hooks/useApiHooks";
import Select from "react-select";
import FormGroup from "./FormGroup";
import { useAgent } from "contexts/v1/masterData/AgentContext";

import Swal from "sweetalert2";
import { useTrip } from "contexts/v1/masterData/TripContext";
import AsyncSelect from "react-select/async";

const QuickCreatePage = ({ sessions, onSuccess, onError }) => {
  const { agents = { data: [] } } = useAgent();
  const agentList = agents.data || [];

  const { trips, updateFilterTrip } = useTrip(); // 🚀 Gunakan TripContext

  const API_URL = "/reservation/quick";
  const { createData: createBooking } = useCreateData(API_URL, "bookings");

  const [formData, setFormData] = useState({
    reservation_date: new Date().toISOString().split("T")[0],
    trip_id: "",
    agent_id: "",
  });

  // ✅ Perbarui TripContext saat tanggal berubah

  const handleFormChange = useFormChangeHandlerV2(setFormData);

  const handleChangeVold = (e) => {
    const { id, value } = e.target;

    // Jika user mengganti tanggal, update di TripContext
    if (id === "reservation_date") {
      updateFilterTrip("date", value); // 🔹 Update tanggal di TripContext
    }

    // Perbarui formData seperti biasa
    // useFormChangeHandlerV2(setFormData)(e);
    // Gunakan fungsi handler yang sudah dipanggil sebelumnya
    handleFormChange(e);
  };

  const handleChange = (e, fieldName) => {
    // 🛑 Cek apakah event berasal dari React-Select atau input biasa
    if (fieldName) {
      // ✅ Jika dari React-Select, gunakan fieldName sebagai id
      setFormData((prev) => ({ ...prev, [fieldName]: e.value }));
      return;
    }

    // ✅ Jika dari input biasa, gunakan e.target
    const { id, value } = e.target;

    // Jika user mengganti tanggal, update di TripContext
    if (id === "reservation_date") {
      updateFilterTrip("date", value); // 🔹 Update tanggal di TripContext
    }

    // setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // 🔹 Handle perubahan input
  const handleChangeAgent = (value, fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  // const handleChange = useFormChangeHandlerV2(setFormData);

  // 🔹 Mapping data trip dari TripContext ke Select Options
  const sessionOptions =
    trips?.data?.map((trip) => ({
      value: trip.id,
      label: `${trip.name} (${trip.start_time} - ${trip.end_time}) - Sisa ${trip.remaining}`,
      remaining: trip.remaining,
    })) || [];

  const agentOptions = agentList.map((agent) => ({
    value: agent.id,
    label: agent.name,
  }));

  // 🔹 Fungsi untuk load agent dari API
  const loadAgentOptions = useCallback(
    async (inputValue, callback) => {
      const filteredAgents = agents.data.filter((agent) =>
        agent.name.toLowerCase().includes(inputValue.toLowerCase())
      );

      callback(
        filteredAgents.map((agent) => ({
          value: agent.id,
          label: agent.name,
        }))
      );
    },
    [agents]
  );

  // ✅ Fungsi untuk cek availability langsung dari Context (tanpa API tambahan)
  const checkAvailability = () => {
    const selectedTrip = trips.data.find(
      (trip) => trip.id === formData.trip_id
    );
    if (!selectedTrip) {
      Swal.fire("Error", "Trip tidak ditemukan!", "error");
      return false;
    }

    console.log("🚀 Trip Availability:", selectedTrip);

    // 🛑 Jika sisa slot tidak cukup untuk 10 booking
    if (selectedTrip.remaining < 10) {
      Swal.fire(
        "Fully Booked",
        `${selectedTrip.name} hanya tersisa ${selectedTrip.remaining} slot.`,
        "error"
      );
      return false;
    }

    return true;
  };

  // ✅ Fungsi untuk mengirim booking setelah cek availability
  const handleBulkSubmit = async (e) => {
    e.preventDefault();

    if (!formData.trip_id || !formData.agent_id) {
      Swal.fire("Warning", "Pilih Trip dan Agent terlebih dahulu!", "warning");
      return;
    }

    // 🔍 Cek ketersediaan sebelum booking
    const isAvailable = checkAvailability();
    if (!isAvailable) return;

    const bookings = Array.from({ length: 10 }, (_, index) => ({
      reservation_date: formData.reservation_date,
      trip_id: formData.trip_id,
      agent_id: formData.agent_id,
      guest_name: `Guest ${index + 1}`,
      guest_phone: `081234567${index % 10}`,
      guest_pax: Math.floor(Math.random() * 5) + 1, // Random 1-5 pax
    }));

    try {
      for (let i = 0; i < bookings.length; i++) {
        console.log(`📢 Mengirim booking ${i + 1}...`);
        await createBooking(bookings[i]); // Kirim request
        await new Promise((resolve) => setTimeout(resolve, 100)); // Delay 100ms
      }
      onSuccess();
      Swal.fire("Success!", "10 Bookings created!", "success");
    } catch (error) {
      console.error("❌ Gagal membuat booking:", error);
      onError();
      Swal.fire("Error!", "Failed to create bookings!", "error");
    }
  };

  return (
    <form onSubmit={handleBulkSubmit} className="d-flex flex-column">
      <div className="action-sheet-content flex-grow-1">
        <div className="card mb-3">
          <div className="card-header">
            <h5 className="card-title">Quick Booking</h5>
          </div>
          <div className="card-body">
            <FormGroup
              id="reservation_date"
              label="Booking Date"
              type="date"
              value={formData.reservation_date}
              onChange={handleChange}
            />
            <FormGroup
              id="trip_id"
              label="Select Trip"
              as="select"
              value={
                sessionOptions.find((opt) => opt.value === formData.trip_id) ||
                null
              }
              onChange={(selected) => handleChange(selected, "trip_id")}
              options={sessionOptions}
              placeholder="Select a Trip"
            />
            {/* <FormGroup
              id="agent_id"
              label="Select Agent"
              as="select"
              value={
                agentOptions.find((opt) => opt.value === formData.agent_id) ||
                null
              }
              onChange={(selected) => handleChange(selected, "agent_id")}
              options={agentOptions}
              placeholder="Select an Agent"
            /> */}
            {/* Async Select Agent */}
            <div className="row mb-3 align-items-center">
              <label
                className="col-sm-4 col-form-label d-flex justify-content-end"
                style={{ fontSize: "14px" }}
              >
                Select Agent:
              </label>
              <div className="col-sm-8">
                <AsyncSelect
                  cacheOptions
                  loadOptions={loadAgentOptions}
                  defaultOptions={agents.data.map((agent) => ({
                    value: agent.id,
                    label: agent.name,
                  }))} // ✅ Pastikan defaultOptions ada
                  value={
                    formData.agent_id
                      ? {
                          value: formData.agent_id,
                          label:
                            agents.data.find(
                              (agent) => agent.id === formData.agent_id
                            )?.name || "Selected Agent",
                        }
                      : null
                  }
                  onChange={(selected) =>
                    handleChangeAgent(selected?.value || "", "agent_id")
                  }
                  isClearable
                  placeholder="Search Agent..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="form-group mt-auto d-grid">
        <button type="submit" className="btn btn-primary btn-block">
          Create 10 Bookings (Delay 100ms)
        </button>
      </div>
    </form>
  );
};

export default QuickCreatePage;
